// Round 1 (C1): ONE shared store per shell badge that LeftMenu and ModuleBar both showed and each fetched on its own:
//   Alerts      = bands firing bad/very-bad (alerts/live-state) + price alerts + feed-runout sites + customers over credit (4 feeds)
//   Review&Post = GET /planned-transactions/count (was the WHOLE planned-transactions payload, counted in the browser)
//   Approvals   = GET /review/pending/count (admin page)
// Loaded after first paint, at most once per 60 s while someone shows it, never while the tab is hidden; any write through
// the api marks it stale (the next mount or module change reloads). Fail-soft: a failed read hides the badge (null), never breaks the menu.
// Round 4e: every read goes through shellRead (one GET /api/shell/badges for all the shell's reads, flushed after paint: it is the deferral now).
import { useState, useEffect } from 'react';
import api, { onDataMutation } from '../services/api';
import { overCount } from '../components/alerts/creditAlertDisplay.mjs';
import { shellRead } from './shellRead';

const TTL = 60000;
// the signed-in user (JWT sub), so a token refresh keeps the count and a different login never sees the previous user's
const who = () => { try { return JSON.parse(atob(String(api.token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).sub || api.token; } catch { return api.token || null; } };

function sharedCount(load) {
  let value = null, at = 0, inflight = null, owner = null;   // owner = who() the value was read for (Codex S2: never show the last user's count)
  const subs = new Set();
  onDataMutation(() => { at = 0; });
  const current = () => (owner === who() ? value : null);
  const refresh = () => {
    if (owner !== who()) { value = null; at = 0; }
    if (inflight || (at && Date.now() - at < TTL) || document.visibilityState === 'hidden' || !api.token) return;
    const me = who();
    inflight = Promise.resolve().then(load).then(v => v, () => null)
      .then(v => { if (me !== who()) return; value = v; owner = me; at = Date.now(); subs.forEach(fn => fn(v)); })
      .finally(() => { inflight = null; });
  };
  // `key` = the caller's refresh trigger (LeftMenu: the module), so a module change re-checks staleness like the old per-module fetch.
  return function useSharedCount(enabled = true, key = '') {
    const [n, setN] = useState(current);
    useEffect(() => {
      if (!enabled) return undefined;
      subs.add(setN); setN(current());
      refresh();
      return () => { subs.delete(setN); };
    }, [enabled, key]);
    return n;
  };
}

export const useAlertsCount = sharedCount(() => Promise.all([
  shellRead('alert_bands').catch(() => null),
  shellRead('price_alerts').catch(() => null),
  shellRead('feed_runout').catch(() => null),
  shellRead('credit_over').catch(() => null),
]).then(([r, p, f, c]) => {
  const bands = new Set(((r && r.rows) || []).filter(x => x.decision === 'bad' || x.decision === 'very-bad').map(x => x.band_id)).size;
  const price = (p && p.rollup && p.rollup.count) || 0;
  const feed = (f && f.rollup && f.rollup.count) || 0;   // 43033: feed-runout sites at risk
  return bands + price + feed + overCount(c);   // GES-389: customers over credit limit (over only; near is not counted)
}));

export const usePlannedCount = sharedCount(() => shellRead('planned').then(r => (r && r.count != null ? Number(r.count) : null)));

export const useReviewCount = sharedCount(() => shellRead('review').then(c => c?.count ?? null));

// 47284: Data Refreshes badge = { error, warn, error_jobs, warn_jobs } from ONE admin read (info verdicts never count); same TTL/poll rules as the other badges.
export const useRefreshHealth = sharedCount(() => shellRead('refresh_health'));
